// Client-side PWA install-funnel telemetry. All decision logic lives here so it
// is unit-testable; the React components/store and App.tsx are thin hook sites.
// Fire-and-forget by contract: a failed send must never break the install flow
// or navigation. Mirrors the first-party pageViewTracking.ts pattern.
//
// The body carries ONLY { event, platform?, browser?, source? }. app/appId are
// derived server-side from the site token; domain/siteVersion are deliberately
// not sent (the server does not persist them — unbounded cardinality).

import { baseUrl } from "./index";
import { getTenantTokenForAppId } from "./jwt2json";
import { isStandalone } from "./displayMode";
import {
  PWA_STANDALONE_LAUNCH,
  PWA_REPEAT_STANDALONE_LAUNCH,
} from "../constants/pwaEvents";

export interface PwaEventDimensions {
  source?: string;
  platform?: string;
  browser?: string;
}

export interface PwaEventPayload {
  event: string;
  platform: string;
  browser: string;
  source?: string;
}

// Distinguishes a device's first standalone launch (the iOS install proxy, since
// iOS fires no `appinstalled`) from subsequent launches. Standalone localStorage
// is partitioned from the browser tab, so the first post-install launch is
// correctly treated as "first".
const STANDALONE_SEEN_KEY = "pwa_standalone_seen";

// Best-effort platform from the UA. The server clamps anything out of its enum
// (ios/android/desktop/unknown) to "unknown", so a miss never drops an event.
// UA is an argument so the function is pure and testable.
export function detectPlatform(ua: string = navigator.userAgent): string {
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

// Best-effort browser from the UA. Order matters: Edge/Samsung/Opera UAs also
// contain "Chrome", and Chrome's UA also contains "Safari", so the most specific
// tokens are checked first. Out-of-enum values are clamped to "unknown" server-side.
export function detectBrowser(ua: string = navigator.userAgent): string {
  if (/Edg(A|iOS|e)?\//i.test(ua)) return "edge";
  if (/SamsungBrowser\//i.test(ua)) return "samsung";
  if (/OPR\/|Opera/i.test(ua)) return "opera";
  if (/Firefox\/|FxiOS\//i.test(ua)) return "firefox";
  if (/Chrome\/|CriOS\//i.test(ua)) return "chrome";
  if (/Safari\//i.test(ua)) return "safari";
  return "unknown";
}

// platform/browser are always included (derived from the UA); source only when a
// caller supplies it. app/appId/domain/siteVersion are intentionally never set.
export function buildPwaEventPayload(
  event: string,
  extra: PwaEventDimensions = {}
): PwaEventPayload {
  const payload: PwaEventPayload = {
    event,
    platform: extra.platform ?? detectPlatform(),
    browser: extra.browser ?? detectBrowser(),
  };
  if (extra.source) payload.source = extra.source;
  return payload;
}

// Fire a single PWA funnel event. No site token → no-op (same gate as
// applyPwaHeadTags): without identity there is nothing to attribute the event to.
export function logPwaEvent(event: string, extra: PwaEventDimensions = {}): void {
  const token = getTenantTokenForAppId();
  if (!token) return;

  const url = `${baseUrl}/analytics/pwa-event?token=${encodeURIComponent(token)}`;
  const body = JSON.stringify(buildPwaEventPayload(event, extra));

  // Primary transport: sendBeacon survives the page being backgrounded or
  // replaced by the installed app. It posts text/plain, which the server's
  // route-level parser accepts.
  try {
    if (
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function" &&
      navigator.sendBeacon(url, body)
    ) {
      return;
    }
  } catch {
    // fall through to the fetch fallback
  }

  // Fallback for browsers without sendBeacon (or when it refuses the payload):
  // keepalive lets the request outlive the page. JSON body → app-wide JSON parser.
  // Both the missing-fetch guard and the try/catch keep the no-op truly
  // fire-and-forget — a synchronous throw must never surface to install/navigation.
  if (typeof fetch !== "function") return;
  try {
    fetch(url, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body,
    }).catch(() => {
      // Tracking must never break install/navigation.
    });
  } catch {
    // A synchronous fetch throw must be swallowed too.
  }
}

// Returns the standalone-launch event for this load, or null when not running
// standalone. First launch on this device marks the flag and reports
// pwa_standalone_launch; later launches report pwa_repeat_standalone_launch.
export function resolveStandaloneLaunchEvent(): string | null {
  if (!isStandalone()) return null;
  if (localStorage.getItem(STANDALONE_SEEN_KEY)) {
    return PWA_REPEAT_STANDALONE_LAUNCH;
  }
  localStorage.setItem(STANDALONE_SEEN_KEY, "true");
  return PWA_STANDALONE_LAUNCH;
}

// App-mount hook: emit the appropriate standalone-launch event when the app is
// running as an installed PWA.
export function logStandaloneLaunchOnMount(): void {
  const event = resolveStandaloneLaunchEvent();
  if (event) logPwaEvent(event);
}
